import React, { useState, useEffect, useRef } from 'react';
import { TOKENS } from '../tokens.js';
import { api } from '../api.js';

const iconBtn = {
  width: 40, height: 40, borderRadius: 20,
  border: 'none', background: 'rgba(251,248,242,0.1)', color: TOKENS.paper,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontSize: 18, lineHeight: 1,
};

function fmt(s) {
  if (!isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${ss}`;
}

export default function WatchPlayer({ onExit }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [montage, setMontage] = useState(null);
  const [singleTime, setSingleTime] = useState(0);
  const [chaptersOpen, setChaptersOpen] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.montage()
      .then((m) => { if (!cancelled) { setMontage(m); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setErr(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingScreen />;
  if (err) return <ErrorScreen error={err} onExit={onExit} />;
  if (!montage?.single_url) return <ErrorScreen error="Montage not available" onExit={onExit} />;

  const chapters = montage.chapters || [];
  const activeChapter = chapters.length
    ? chapters.findIndex((c, i) =>
        singleTime >= c.start_s && (i === chapters.length - 1 || singleTime < chapters[i + 1].start_s)
      )
    : -1;

  const seekTo = (s) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = s;
    v.play().catch(() => {});
    setChaptersOpen(false);
  };

  const activeName = activeChapter >= 0 ? chapters[activeChapter].title : '';

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <button onClick={onExit} aria-label="Close"
        style={{ position: 'absolute', top: 'max(16px, env(safe-area-inset-top))', left: 16, zIndex: 20, ...iconBtn }}>←</button>

      {chapters.length > 0 && (
        <div style={{
          position: 'absolute', top: 'max(16px, env(safe-area-inset-top))', left: 70, right: 70,
          zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        }}>
          <button onClick={() => setChaptersOpen((v) => !v)}
            style={{
              background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(251,248,242,0.25)',
              color: TOKENS.paper, padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
              fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8,
              backdropFilter: 'blur(8px)', maxWidth: '100%',
            }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {activeName ? `Ch ${activeChapter + 1} · ${activeName}` : 'Chapters'}
            </span>
          </button>
        </div>
      )}

      <video ref={videoRef} controls playsInline autoPlay
        onTimeUpdate={(e) => setSingleTime(e.currentTarget.currentTime)}
        src={montage.single_url}
        style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />

      {chapters.length > 0 && (
        <div style={{
          position: 'absolute', left: 12, right: 12, bottom: 70,
          display: 'flex', gap: 3, height: 6, borderRadius: 4, overflow: 'hidden',
          background: 'rgba(0,0,0,0.45)', padding: 3, pointerEvents: 'auto', zIndex: 10,
        }}>
          {chapters.map((c, i) => {
            const len = Math.max(0.1, c.end_s - c.start_s);
            const isActive = i === activeChapter;
            const past = singleTime >= c.end_s;
            const inProg = isActive ? (singleTime - c.start_s) / len : past ? 1 : 0;
            return (
              <button key={i} onClick={() => seekTo(c.start_s)}
                title={`${fmt(c.start_s)} · ${c.title}`}
                style={{
                  flex: len, position: 'relative', overflow: 'hidden', borderRadius: 2,
                  background: 'rgba(251,248,242,0.18)', border: 'none', padding: 0, cursor: 'pointer',
                }}>
                <div style={{
                  position: 'absolute', inset: 0, width: `${inProg * 100}%`,
                  background: isActive ? TOKENS.orange : 'rgba(251,248,242,0.55)',
                  transition: isActive ? 'none' : 'width 200ms',
                }} />
              </button>
            );
          })}
        </div>
      )}

      {chaptersOpen && chapters.length > 0 && (
        <div onClick={() => setChaptersOpen(false)}
          style={{
            position: 'absolute', inset: 0, zIndex: 30,
            background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(6px)',
            display: 'flex', flexDirection: 'column', padding: 'max(20px, env(safe-area-inset-top)) 16px 80px',
            color: TOKENS.paper, overflow: 'hidden',
          }}>
          <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', opacity: 0.6, marginBottom: 12, textAlign: 'center' }}>
            Chapters
          </div>
          <div onClick={(e) => e.stopPropagation()} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {chapters.map((c, i) => (
              <button key={i} onClick={() => seekTo(c.start_s)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: i === activeChapter ? 'rgba(229,117,31,0.18)' : 'rgba(251,248,242,0.06)',
                  border: i === activeChapter ? '1px solid rgba(229,117,31,0.6)' : '1px solid rgba(251,248,242,0.1)',
                  color: TOKENS.paper, padding: '14px 18px', borderRadius: 12,
                  fontSize: 16, textAlign: 'left', cursor: 'pointer',
                }}>
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 12, minWidth: 0 }}>
                  <span style={{ opacity: 0.45, fontVariantNumeric: 'tabular-nums', minWidth: 22, textAlign: 'right' }}>{i + 1}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</span>
                </span>
                <span style={{ opacity: 0.6, fontVariantNumeric: 'tabular-nums', marginLeft: 12, fontSize: 13 }}>{fmt(c.start_s)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', color: TOKENS.paper, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="hand" style={{ fontSize: 32, color: TOKENS.orange }}>loading the montage…</div>
    </div>
  );
}

function ErrorScreen({ error, onExit }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000', color: TOKENS.paper, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center' }}>
      <div className="display" style={{ fontSize: 44, fontStyle: 'italic', marginBottom: 12 }}>Hmm.</div>
      <div style={{ fontSize: 15, opacity: 0.7, maxWidth: 400, marginBottom: 24 }}>{error}</div>
      <button className="btn" onClick={onExit}>Back</button>
    </div>
  );
}
